/** Dashboard side-panel data — wire to API when endpoints are available. */

/** Local AffiliatePromotions fallback pictures (randomly assigned). */
export const AFFILIATE_PROMO_IMAGES = [
  '/images/promotions/special-promotion.png',
  '/images/promotions/ecommerce-60-off.png',
  '/images/promotions/sale-money-off.png',
];

/** Stable pick from the 3 promo images (same seed → same image). */
export const pickAffiliatePromoImage = (seed = '') => {
  const key = String(seed || 'promo');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % AFFILIATE_PROMO_IMAGES.length;
  }
  return AFFILIATE_PROMO_IMAGES[hash];
};

/** Shuffle the 3 images into carousel slides (random order per call). */
export const buildAffiliatePromoSlides = (link = '/promotions/list') => {
  const images = [...AFFILIATE_PROMO_IMAGES];
  for (let i = images.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = images[i];
    images[i] = images[j];
    images[j] = tmp;
  }
  return images.map((image, index) => ({
    id: `promo-slide-${index + 1}`,
    image,
    link,
  }));
};

export const DASHBOARD_PROMO_SLIDES = AFFILIATE_PROMO_IMAGES.map((image, index) => ({
  id: `promo-slide-${index + 1}`,
  image,
  link: '/promotions/list',
}));

/** Dummy until admin Account Manager API is wired. */
export const DUMMY_ACCOUNT_MANAGER = {
  name: 'Manager',
  email: 'Manager@abetglobal.com',
  phone: '+44122345688',
};

export const parseAccountManager = (source = {}) => {
  const data = source?.result ?? source ?? {};
  const nested =
    data.accountManagerInfo ||
    data.AccountManagerInfo ||
    data.accountManager ||
    data.AccountManager ||
    data.manager ||
    data.Manager ||
    null;
  const mgr = nested && typeof nested === 'object' ? nested : data;

  const name =
    (typeof nested === 'string' ? nested : null) ||
    mgr.accountManagerName ||
    mgr.AccountManagerName ||
    mgr.managerName ||
    mgr.ManagerName ||
    mgr.fullName ||
    mgr.FullName ||
    mgr.name ||
    mgr.Name ||
    '';
  const email =
    mgr.accountManagerEmail ||
    mgr.AccountManagerEmail ||
    mgr.managerEmail ||
    mgr.ManagerEmail ||
    mgr.amEmail ||
    mgr.email ||
    mgr.Email ||
    '';
  const phone =
    mgr.accountManagerPhone ||
    mgr.AccountManagerPhone ||
    mgr.managerPhone ||
    mgr.ManagerPhone ||
    mgr.accountManagerWhatsApp ||
    mgr.managerWhatsApp ||
    mgr.whatsApp ||
    mgr.WhatsApp ||
    mgr.mobileNumber ||
    mgr.MobileNumber ||
    mgr.phone ||
    mgr.Phone ||
    mgr.amPhone ||
    mgr.amWhatsApp ||
    '';

  // Ignore when nested object is actually something else and no contact fields exist
  if (!String(name || '').trim() && !String(email || '').trim() && !String(phone || '').trim()) {
    return null;
  }

  // Avoid treating the whole user profile as manager (email alone from user profile)
  const looksLikeUserProfile =
    !nested &&
    (data.firstName || data.lastName || data.affiliateCode || data.partnerUrl) &&
    !data.accountManagerEmail &&
    !data.managerEmail &&
    !data.AccountManagerEmail;
  if (looksLikeUserProfile) return null;

  return {
    name: String(name || '').trim() || 'Account Manager',
    email: String(email || '').trim(),
    phone: String(phone || '').trim(),
  };
};

const isClientVerified = (client) => {
  const flags = [
    client?.isVerified,
    client?.IsVerified,
    client?.accountVerified,
    client?.verified,
  ];
  if (flags.some((v) => v === true || v === 1 || String(v).toLowerCase() === 'true')) return true;
  if (flags.some((v) => v === false || v === 0 || String(v).toLowerCase() === 'false')) return false;

  const kyc = String(
    client?.userKYCStatus ?? client?.UserKYCStatus ?? client?.kycStatus ?? client?.docsStatus ?? ''
  )
    .toLowerCase()
    .replace(/\s+/g, '');
  return kyc === 'verified' || kyc === 'approved';
};

const formatSignupDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/** ISO alpha-2 only (e.g. PK, US). Anything else → —. */
const getTwoLetterCountry = (item) => {
  const raw = item?.countryCode ?? item?.CountryCode ?? item?.countryIso ?? item?.CountryIso ?? '';
  const code = String(raw).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : '—';
};

const getClientCode = (client) => {
  const code =
    client?.clientCode ??
    client?.ClientCode ??
    client?.userCode ??
    client?.UserCode ??
    null;
  return code != null && String(code).trim() !== '' ? String(code).trim() : null;
};

/**
 * One row per referred client for Traders widget (GetClientsList):
 * ClientCode | Country (ISO-2) | Sign-Up Date (+ KYC verified icon).
 * Top N only (default 10). Does not expand MT4/MT5 trading accounts.
 */
export const mapClientsToTraders = (clients = [], limit = 10) => {
  const rows = [];
  const seen = new Set();

  for (let clientIndex = 0; clientIndex < clients.length; clientIndex += 1) {
    if (rows.length >= limit) break;

    const client = clients[clientIndex];
    const clientCode = getClientCode(client);
    if (!clientCode) continue;

    const key = clientCode.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    rows.push({
      id: `client-${clientCode}-${clientIndex}`,
      clientCode,
      country: getTwoLetterCountry(client),
      signUpDate: formatSignupDate(
        client.userRegistrationDate ??
          client.UserRegistrationDate ??
          client.registrationDate ??
          client.signUpDate ??
          client.createdDate ??
          null
      ),
      verified: isClientVerified(client),
    });
  }

  return rows;
};
